import { ArrowRight } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
    emailResendCode,
    emailVerify,
    getAuthError,
    getPendingEmail,
    getUserLoadingState,
    setAuthFlow
} from '../../../store/slices/authSlice';
import CustomButton, { BUTTON_STYLE_TYPE } from '../../common/Button';
import ArrowLeftIcon from '../../icons/ArrowLeftIcon';
import OtpCodeInput from './OtpCodeInput';
import styles from './styles.module.scss';

const CODE_LENGTH = 6;

const VerifyCodeStep = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const email = useSelector(getPendingEmail);
    const loading = useSelector(getUserLoadingState);
    const error = useSelector(getAuthError);
    const [code, setCode] = useState('');
    const [resendCooldown, setResendCooldown] = useState(0);

    const onSubmit = (event) => {
        event.preventDefault();
        if (loading || code.length !== CODE_LENGTH) {
            return;
        }
        dispatch(emailVerify({ email, code }));
    };

    const onResend = () => {
        if (resendCooldown > 0) {
            return;
        }
        dispatch(emailResendCode({ email, purpose: 'signup' })).then((action) => {
            const retryAfter = action.payload?.data?.retryAfter || 60;
            setResendCooldown(retryAfter);
            const timer = setInterval(() => {
                setResendCooldown((current) => {
                    if (current <= 1) {
                        clearInterval(timer);
                        return 0;
                    }
                    return current - 1;
                });
            }, 1000);
        });
    };

    return (
        <>
            {/* HORO-10509: single-row header (back arrow + centered title),
                same treatment as Sign in/Set password — confirmed via
                Figma's inspector on both the mobile and desktop "Verify
                your email" frames (arrow and title share one row there,
                not stacked). */}
            <div className={styles.signInHeader}>
                <button
                    type="button"
                    className={styles.backIconBtn}
                    aria-label={t('buttons.back')}
                    onClick={() => dispatch(setAuthFlow('entry'))}
                >
                    <ArrowLeftIcon width={24} height={24} />
                </button>
                <h2 className={`${styles.title} ${styles.titleMixedCase}`}>{t('modal.verifyEmailTitle')}</h2>
                <div className={styles.headerSpacer} aria-hidden="true" />
            </div>
            <p className={styles.subtitle}>
                {t('modal.verifyEmailLabel', { email })}{' '}
                <button type="button" className={styles.editEmailLink} onClick={() => dispatch(setAuthFlow('entry'))}>
                    ({t('modal.editEmail')})
                </button>
            </p>
            <form onSubmit={onSubmit}>
                <OtpCodeInput value={code} onChange={setCode} disabled={loading} />
                {error && <p className={styles.formError}>{error.message}</p>}
                <div className={styles.submitBtnWrap}>
                    <CustomButton
                        text={t('buttons.verify')}
                        loading={loading}
                        disable={loading || code.length !== CODE_LENGTH}
                        styleType={BUTTON_STYLE_TYPE.PURPLE}
                    />
                </div>
            </form>
            {/* HORO-10509: this was only ever rendering `resendCode` ("Didn't
                get it?") as the button's own label — `resendCodeAction`
                ("Resend"), sitting right next to it in i18n, was never
                actually used. Same static-question + bold-action-word
                pattern as the other helperText footers (EntryStep's
                "Already have an account? Sign in", etc). */}
            <p className={styles.helperText}>
                {t('modal.resendCode')}{' '}
                <button type="button" className={styles.helperTextAction} onClick={onResend} disabled={resendCooldown > 0}>
                    {resendCooldown > 0 ? t('modal.resendCodeThrottled', { seconds: resendCooldown }) : t('modal.resendCodeAction')}
                </button>
            </p>
        </>
    );
};

export default VerifyCodeStep;
